import { GetTermsQuery, TaxonomyEnum } from 'app/services/api'
import { distinctArray, parseImgMedia } from 'helpers'
import {
  TaxonomyId,
  TaxonomyState,
  TaxSliceState,
  Term,
  TermCollection,
  TermEntityAdapter,
  TermId,
  TermList,
  // TermResult,
  TermTree
} from 'types'

/**
 * Type Guard for {@link Term}
 */
// const isTerm = (x: TermResult): x is Term => {
//   if (!x.id) return false
//   if (!x.name) return false
//   return true
// }


/**
 * TypeGuard for {@link TaxonomyId}
 */
export const isTaxonomyId = (x: any): x is TaxonomyId => {
  return (
    typeof x === 'string' &&
    Object.values(TaxonomyEnum)
      .map((v) => `${v}`)
      .includes(x)
  )
}

/**
 * TypeGuard for {@link TaxonomyState}
 */
export const isTaxonomyState = (x: any): x is TaxonomyState<TaxonomyId> => {

  const keys = Object.keys(x)

  return keys.includes('taxonomyId') &&
    isTaxonomyId(x.taxonomyId) &&
    keys.includes('termsLoaded') &&
    keys.includes('selections') &&
    keys.includes('status')
}


/**
 * TypeGuard for {@link TaxonomyEnum}
 */
export const isTaxonomyEnum = (x: any): x is TaxonomyEnum => {
  return (
    typeof x === 'string' &&
    Object.values(TaxonomyEnum)
      .map((v) => `${v}`)
      .includes(x)
  )
}

/**
 * Cast {@link TaxonomyId} as {@link TaxonomyEnum} (TaxonomyId is a value of TaxonomyEnum)
 */
export const asTaxonomyEnum = (x: TaxonomyId): TaxonomyEnum => x as TaxonomyEnum

////////////////////////////////////////////////////////////////////////////////////

/**
 * Recursively builds the `TermTree` from a `TermCollection`
 */
export function buildTermTree<T extends TaxonomyId>(
  taxState: TaxonomyState<T>,
  entities: TermEntityAdapter
): TermTree {
  const { selectAll } = entities.getSelectors()

  const termsFrom = selectAll(taxState)

  const buildBranch = (parent?: TermId) => {
    return [...termsFrom].reduce(
      (results, t, index) => {

        const match = t.parentId === parent || (!t.parentId && !parent)

        if (match) {
          termsFrom.slice(index, 1)
          const children = buildBranch(t.id)
          results.push({ id: t.id, children })
        }

        return results;
      },
      [] as TermTree
    )
  }

  return buildBranch();
}


/**
 * Recursively extracts all `TermId`s from a `TermTree` into a flat array
 */
export function flattenTermTree(termTree: TermTree): TermList {
  return termTree.reduce((values, { id, children = [] }) => {
    return [...values, ...flattenTermTree(children), id]
  }, [] as TermList)
}

/**
 * Retrieve an array of a {@link TermId} and its ancestors
 */
export const getTermAncestors = (
  termId: TermId,
  terms: TermCollection
): TermList => {
  const results: TermList = []
  let p: TermId | undefined = termId
  while (p && !results.includes(p)) {
    results.push(p)
    const term: Term | undefined = terms[p]
    p = term && term.parentId
  }
  return results
}

/**
 * From a given {@link TermTree}, recursively extracts the TermIds for any Term without children
 */
export function getTermTreeLeaves(termTree: TermTree): TermList {
  return termTree.reduce((values, { id, children = [] }) => {
    return values.concat(children.length ? getTermTreeLeaves(children) : id)
  }, [] as TermList)
}

/**
 * Recursively creates a TermTree representing only Terms listed in taxonomy.selections
 * @param taxonomy Any hierarchical TaxonomyState
 * @param branch Defaults to provided taxonomy.termTree
 */
export function getSelectionsTermTree<T extends TaxonomyId>(
  taxonomy: TaxSliceState<T>,
  branch?: TermTree
): TermTree {
  const { selections } = taxonomy
  const termTree = branch ?? taxonomy.termTree

  return (
    termTree
      // Only selected terms
      .filter(({ id }) => selections.includes(id))
      // Recursive over children
      .map(({ id, children }) => {
        const c = getSelectionsTermTree(taxonomy, children)
        return { id, children: c }
      })
  )
}

/**
 * Use type guard to check query results
 */
export const sanitizeTermQueryResults = <
  Q extends GetTermsQuery = GetTermsQuery
>(
  data: Q
): Term[] => {

  const terms = data.terms?.edges || []

  const results = (
    terms.reduce((results, termResult) => {
      const maybeTerm = termResult?.node

      if (!maybeTerm) return results

      const taxonomyId = maybeTerm.__typename ? (maybeTerm.__typename.toUpperCase() as TaxonomyId) : null

      if (!taxonomyId) return results

      let hide: boolean = false
      let customTitle: string | undefined
      let image
      let defaultImage
      let website
      let websiteLabel
      let brandStockistIds: number[] = [];

      if (maybeTerm.__typename === 'ProductCategory') {
        hide = maybeTerm?.termSettings?.hide ? true : false
        customTitle = maybeTerm?.termSettings?.customTitle || undefined
        image = parseImgMedia(maybeTerm?.termSettings?.image)
        defaultImage = parseImgMedia(maybeTerm?.termSettings?.defaultImage)
      }
      if (maybeTerm.__typename === 'Stockist' || maybeTerm.__typename === 'Brand') {
        image = parseImgMedia(maybeTerm.brandSettings?.image)
        website = maybeTerm.brandSettings?.website || undefined
        websiteLabel = maybeTerm.brandSettings?.websiteLabel || undefined
      }
      if (maybeTerm.__typename === 'Brand') {
        hide = maybeTerm?.termSettings?.hide ? true : false
        customTitle = maybeTerm?.termSettings?.customTitle || undefined
        if (maybeTerm.brandStockist?.stockist?.length) {
          maybeTerm.brandStockist?.stockist?.map((stockist) => {
            if (stockist && stockist.stockistId) brandStockistIds = [...brandStockistIds, stockist.stockistId];
          });
        }
      }

      const isKeyOf = <K extends string>(maybeKey: K, x: Record<string, any>): x is { [P in K]: any } => {
        return Object.keys(x).includes(maybeKey)
      }

      if (hide) return results

      const id = maybeTerm.id
      const name = customTitle ? customTitle : maybeTerm.name ?? ''
      const count = maybeTerm.count ?? 0
      const slug = maybeTerm.slug ?? ''
      const uri = maybeTerm.uri ?? ''
      const parentId = isKeyOf('parentId', maybeTerm) && maybeTerm.parentId ? maybeTerm.parentId : 0

      const term: Term = {
        taxonomyId,
        slug,
        uri,
        id,
        name,
        image,
        defaultImage,
        website,
        websiteLabel,
        count,
        parentId,
        hide,
        brandStockistIds,
      }

      return results.concat(term)
    }, [] as Term[]) || []
  )

  return results;
}

/**
 * Returns array of distinct TermIds with orphaned terms removed
 * @param taxonomy
 */
export function validateSelections<T extends TaxonomyId>( taxonomy: TaxonomyState<T> ): TermList {

  let { selections } = taxonomy

  if (!selections.length) {
    const {
      allowNull,
      defaultSelections = [],
      termTree: [firstTerm],
    } = taxonomy
    if (!allowNull && !defaultSelections.length && firstTerm)
      defaultSelections.push(firstTerm.id)
    selections = defaultSelections
  }

  selections = flattenTermTree(
    getSelectionsTermTree({ ...taxonomy, selections })
  )

  return distinctArray(selections)
}
